

import { TemplateProperty } from '../Types/BespokeTypes';
import { getFakeProducts } from './fakeData';
import { getNextEndDate } from './DateUtils';
import { InstagramMediaContentData } from '../../../Services/InstagramServices';


function isJsonString(str) {
	try {
		JSON.parse(str);
	} catch (e) {
		return false;
	}
	return true;
}


export class InstagramMediaValue extends String {
	static fromStringInput(stringInput: InstagramMediaValue) {
		return !stringInput ? Array.from([]) : Array.from(stringInput.split(/[,]/).filter(x => x));
	}
	static fromValues(value: string[] | null) {
		return new InstagramMediaValue(value?.join(','));
	}
}

export class ProductReferencesValue extends String {
	static fromStringInput(stringInput: ProductReferencesValue) {
		return !stringInput ? Array.from([]) : Array.from(stringInput.split(/[;,\n]/).filter(x => x));
	}
}

export class ProductRecommendationsValue extends String {
	static fromStringInput(stringInput: ProductRecommendationsValue | null) {
		return !stringInput ? Array.from([]) : Array.from(isJsonString(stringInput) ? JSON.parse(stringInput as string) : []);
	}
	static fromValue(value) {
		return new ProductRecommendationsValue(JSON.stringify(value));
	}
}


const generateProducts = (ids) => {
	return getFakeProducts(ids.length)
		.map(x => ({
			...x,
			Id: x,
		}));
};


type liquidModelProcessingType = {
	getInstagramMedia?: (
		mediaId: string,
		callbackSuccess: (data: InstagramMediaContentData) => void,
		callbackError?: (error: any, result?: any) => void) => void
};

export function liquidModelProcessing({ getInstagramMedia = undefined }: liquidModelProcessingType) {

	const getInstagramMediaPromise = (mediaId: string): Promise<InstagramMediaContentData> => {
		if (getInstagramMedia === undefined)
			return Promise.resolve({ id: mediaId });
		return new Promise((resolve, _) =>
			getInstagramMedia(mediaId, resolve, _ => resolve({ id: mediaId }))
		);
	};

	const getLiquidModel = async (modelProperties: TemplateProperty[]): Promise<any> => {
		const calculateCountdownProperties = (property, value) => {

			let date;
			const matchHourFormat = value.match(/^(\d?\d:\d\d)-(.*)/g);
			if (matchHourFormat) {
				date = getNextEndDate(value);
			} else {
				date = Date.parse(value);
			}

			const totalSeconds = Math.floor((date - Date.now()) / 1000);
			const getDhms = () => {
				const totalMinutes = Math.floor(totalSeconds / 60);
				const totalHours = Math.floor(totalMinutes / 60);
				const totalDays = Math.floor(totalHours / 24);

				const hours = totalHours % 24;
				const minutes = totalMinutes % 60;
				const seconds = totalSeconds % 60;

				return [totalDays, hours, minutes, seconds, false];
			};
			const format2Digits = (d) => d.toLocaleString('en-US', { minimumIntegerDigits: 2, useGrouping: false });
			const dhms = totalSeconds < 0 ? ['0', '00', '00', '00', true] : getDhms();
			const countDownProperties = {};
			countDownProperties[property + '_DAYS'] = dhms[0];
			countDownProperties[property + '_HOURS'] = format2Digits(dhms[1]);
			countDownProperties[property + '_MINUTES'] = format2Digits(dhms[2]);
			countDownProperties[property + '_SECONDS'] = format2Digits(dhms[3]);
			countDownProperties[property + '_HASFINISHED'] = dhms[4];
			return countDownProperties;
		};

		const getProductShelvingProperty = (shelvingRules) => {
			const nbOfProducts = shelvingRules.map(x => x.NumberOfProducts).reduce((a, b) => a + b, 0);
			const refs = new Array(nbOfProducts).fill(10).map((_, i) => 'REF' + i);
			const property = {};
			property['RecommandationProducts'] = generateProducts(refs);
			return property;
		};

		const getProductReferencesProperty = (productReferences) => {
			const property = {};
			property['Products'] = generateProducts(productReferences.filter(x => x));
			return property;
		};

		const getInstagramMedia = async (propertyName, mediaIds: string[]) => {
			const mediaUrls = [] as InstagramMediaContentData[];
			for (let index = 0; index < mediaIds.filter(x => x).length; index++) {
				const mediaId = mediaIds[index];
				const result = await getInstagramMediaPromise(mediaId)
					.catch((error) => {
						console.log('Get instagram media error', error);
					});
				if (result != null)
					mediaUrls.push(result);
			}
			const property = {};
			property[propertyName + '_PREVIEW'] = mediaUrls;
			return property;
		};

		const addSpecialModelProperties = async (key, value, model) => {
			if (value && value.isCountDown) {
				const countdownProperties = calculateCountdownProperties(key, value);
				for (const [key2, value2] of Object.entries(countdownProperties)) {
					model[key2] = value2;
				}
			}
			if (value && value instanceof InstagramMediaValue) {
				const instagramMedia = await getInstagramMedia(key, InstagramMediaValue.fromStringInput(value)) ?? [] as string[];
				for (const [key2, value2] of Object.entries(instagramMedia)) {
					model[key2] = value2;
				}
			}
			if (value && value instanceof ProductReferencesValue) {
				const contentProductsProperty = getProductReferencesProperty(ProductReferencesValue.fromStringInput(value));
				for (const [key2, value2] of Object.entries(contentProductsProperty)) {
					model[key2] = value2;
				}
			}
			if (value && value instanceof ProductRecommendationsValue) {
				const contentProductsProperty = getProductShelvingProperty(ProductRecommendationsValue.fromStringInput(value));
				for (const [key2, value2] of Object.entries(contentProductsProperty)) {
					model[key2] = value2;
				}
			}
			if (key === 'FormSurveyCustom') {
				model['FormSurveyCustomHtml'] = '<div id="formSurveyCustom"></div>';
			}
		};
		const values = {};
		for (let i = 0; i < modelProperties.length; i++) {
			const modelProperty = modelProperties[i];
			const getModelEntries = async () => {
				const modelEntries = {};
				if (modelProperty.IsGroup === true) {
					const groupProperty = {};
					for (const [key, value] of Object.entries(modelProperty.Value)) {
						groupProperty[key] = value;
						await addSpecialModelProperties(key, value, groupProperty);
					}
					modelEntries[modelProperty.Name] = groupProperty;
					return modelEntries;
				}
				else if (modelProperty.IsCollection === true) {
					const collectionProperty: any[] = [];
					for (let j = 0; j < modelProperty.Value.length; j++) {
						const collectionItem = {};
						collectionProperty.push(collectionItem);
						for (const [key, value] of Object.entries(modelProperty.Value[j])) {
							const name = key;
							collectionItem[name] = value;
							await addSpecialModelProperties(key, value, collectionItem);
						}
					}
					modelEntries[modelProperty.Name] = collectionProperty;
					return modelEntries;
				}
				else {
					modelEntries[modelProperty.Name] = modelProperty.Value;
					await addSpecialModelProperties(modelProperty.Name, modelProperty.Value, modelEntries);
					return modelEntries;
				}
			};

			for (const [key, value] of Object.entries(await getModelEntries())) {
				values[key] = value;
			}
		}

		return Promise.resolve(values);
	};

	return {
		getLiquidModel
	};
}